import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui';
import { generateReel, getJobStatus } from '../api';

type ReelStudioModalProps = {
  workspaceId: number;
  onClose: () => void;
  /** Called once the reel post has been created (to refresh the content list). */
  onCreated?: () => void;
};

type ReelOutput = {
  video_url?: string;
  title?: string;
  tagline?: string;
  wordmark?: string;
};

type Phase = 'form' | 'generating' | 'done' | 'error';

const QUICK_IDEAS = [
  'a Moroccan riad at golden hour',
  'quiet Japanese tea ritual at dawn',
  'old-money Italian summer on the lake',
  'minimalist Scandinavian slow mornings',
];

const STEPS = ['Writing the concept', 'Generating the visuals', 'Editing the reel'];

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: 600,
  fontSize: 'var(--text-sm)',
  marginBottom: 6,
  color: 'var(--text-primary)',
};

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: 10,
  borderRadius: 8,
  border: '1px solid var(--border-color, var(--border))',
  fontFamily: 'inherit',
  fontSize: 'var(--text-sm)',
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
};

export function ReelStudioModal({ workspaceId, onClose, onCreated }: ReelStudioModalProps) {
  const [phase, setPhase] = useState<Phase>('form');
  const [theme, setTheme] = useState('');
  const [count, setCount] = useState(12);
  const [duration, setDuration] = useState(7.5);
  const [overlay, setOverlay] = useState(true);
  const [tagline, setTagline] = useState('');
  const [wordmark, setWordmark] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [error, setError] = useState('');
  const [result, setResult] = useState<ReelOutput | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const jobIdRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const createdNotified = useRef(false);

  const stopTimers = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    pollRef.current = null;
    tickRef.current = null;
  };

  useEffect(() => stopTimers, []);

  if (typeof document === 'undefined') return null;

  const activeStep = elapsed < 7 ? 0 : elapsed < 7 + count * 3 ? 1 : 2;

  const startPolling = (jobId: string) => {
    jobIdRef.current = jobId;
    setElapsed(0);
    tickRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    pollRef.current = setInterval(async () => {
      try {
        const status = await getJobStatus(jobId);
        if (status.status === 'completed') {
          stopTimers();
          setResult((status.output as ReelOutput) || {});
          setPhase('done');
          if (!createdNotified.current) {
            createdNotified.current = true;
            onCreated?.();
          }
        } else if (status.status === 'failed') {
          stopTimers();
          setError(status.error || 'Reel generation failed. Please try again.');
          setPhase('error');
        }
      } catch {
        /* transient — keep polling */
      }
    }, 2500);
  };

  const handleGenerate = async () => {
    setError('');
    const t = theme.trim();
    if (!t) {
      setError('Give your reel a theme to get started.');
      return;
    }
    setPhase('generating');
    try {
      const res = await generateReel(workspaceId, {
        theme: t,
        count,
        duration_seconds: duration,
        overlay,
        tagline: overlay ? tagline.trim() : '',
        wordmark: overlay ? wordmark.trim() : '',
      });
      // The post already exists; refresh the list even while it renders.
      if (!createdNotified.current) {
        createdNotified.current = true;
        onCreated?.();
      }
      startPolling(res.job_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the reel.');
      setPhase('error');
    }
  };

  const resetForm = () => {
    stopTimers();
    createdNotified.current = false;
    setResult(null);
    setError('');
    setPhase('form');
  };

  // ── Renders ────────────────────────────────────────────────────────────────
  const renderForm = () => (
    <>
      <div className="modal-body">
        <div
          style={{
            background: 'var(--color-primary-50)',
            border: '1px solid var(--color-primary-100)',
            borderRadius: 'var(--radius-md, 10px)',
            padding: '12px 14px',
            marginBottom: 18,
          }}
        >
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--text-primary)' }}>One sentence in, a reel out.</strong>{' '}
            Conistra designs a cohesive set of images on your theme and edits them into a
            ready-to-post Instagram reel — tagline, caption and hashtags included. It lands in
            your Content like any other post.
          </p>
        </div>

        <label htmlFor="reel-theme" style={labelStyle}>
          What&apos;s the reel about?
        </label>
        <textarea
          id="reel-theme"
          rows={2}
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
          placeholder="e.g. a quiet Japanese tea ritual at dawn"
          style={{ ...fieldStyle, resize: 'vertical' }}
          autoFocus
        />

        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 6 }}>
            Need inspiration?
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {QUICK_IDEAS.map((idea) => (
              <button
                key={idea}
                type="button"
                onClick={() => setTheme(idea)}
                style={{
                  fontSize: 'var(--text-xs)',
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-full, 999px)',
                  border: '1px solid var(--border-color, var(--border))',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
              >
                {idea}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          style={{
            marginTop: 18,
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'var(--primary)',
            fontWeight: 600,
            fontSize: 'var(--text-sm)',
            cursor: 'pointer',
          }}
        >
          {showAdvanced ? '▾ Customize' : '▸ Customize'}
        </button>

        {showAdvanced && (
          <div
            style={{
              marginTop: 12,
              display: 'grid',
              gap: 14,
              padding: 14,
              background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius-md, 10px)',
            }}
          >
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="reel-count" style={labelStyle}>
                  Images
                </label>
                <select
                  id="reel-count"
                  value={count}
                  onChange={(e) => setCount(Number(e.target.value))}
                  style={fieldStyle}
                >
                  {[8, 10, 12, 15].map((n) => (
                    <option key={n} value={n}>
                      {n} frames
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="reel-duration" style={labelStyle}>
                  Length
                </label>
                <select
                  id="reel-duration"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  style={fieldStyle}
                >
                  {[6, 7.5, 9, 12].map((n) => (
                    <option key={n} value={n}>
                      {n}s
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>
              <input type="checkbox" checked={overlay} onChange={(e) => setOverlay(e.target.checked)} />
              Show text overlay (tagline + brand wordmark)
            </label>

            {overlay && (
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="reel-tagline" style={labelStyle}>
                    Tagline
                  </label>
                  <input
                    id="reel-tagline"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="auto — the agent writes it"
                    style={fieldStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="reel-wordmark" style={labelStyle}>
                    Wordmark
                  </label>
                  <input
                    id="reel-wordmark"
                    value={wordmark}
                    onChange={(e) => setWordmark(e.target.value)}
                    placeholder="auto — your brand name"
                    style={fieldStyle}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 16, marginBottom: 0 }}>
          Uses about {count} image credits. Takes a minute or two to render.
        </p>

        {error && (
          <p style={{ color: 'var(--color-danger-600, #dc2626)', fontSize: 'var(--text-sm)', marginBottom: 0 }}>
            {error}
          </p>
        )}
      </div>
      <div className="modal-footer">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleGenerate}>
          ✨ Create reel
        </Button>
      </div>
    </>
  );

  const renderGenerating = () => (
    <>
      <div className="modal-body" style={{ textAlign: 'center' }}>
        <div style={{ ...phoneFrame, margin: '8px auto 22px' }}>
          <div className="reel-shimmer" style={shimmer} />
        </div>
        <div style={{ display: 'grid', gap: 10, maxWidth: 320, margin: '0 auto', textAlign: 'left' }}>
          {STEPS.map((label, i) => {
            const state = i < activeStep ? 'done' : i === activeStep ? 'active' : 'todo';
            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    color: state === 'todo' ? 'var(--text-secondary)' : '#fff',
                    background:
                      state === 'done'
                        ? 'var(--primary)'
                        : state === 'active'
                          ? 'var(--primary)'
                          : 'var(--bg-secondary)',
                    border: state === 'todo' ? '1px solid var(--border-color, var(--border))' : 'none',
                    opacity: state === 'active' ? 0.85 : 1,
                  }}
                >
                  {state === 'done' ? '✓' : i + 1}
                </span>
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: state === 'active' ? 600 : 400,
                    color: state === 'todo' ? 'var(--text-secondary)' : 'var(--text-primary)',
                  }}
                >
                  {label}
                  {state === 'active' ? '…' : ''}
                </span>
              </div>
            );
          })}
        </div>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 20 }}>
          You can close this and keep working — your reel will appear in Content when it&apos;s ready.
        </p>
      </div>
      <div className="modal-footer">
        <Button variant="secondary" onClick={onClose}>
          Close — keep working
        </Button>
      </div>
    </>
  );

  const renderDone = () => (
    <>
      <div className="modal-body" style={{ textAlign: 'center' }}>
        <div style={{ ...phoneFrame, margin: '4px auto 18px' }}>
          {result?.video_url ? (
            <video
              src={result.video_url}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              autoPlay
              loop
              muted
              playsInline
              controls
            />
          ) : (
            <div style={shimmer} />
          )}
        </div>
        {result?.title && (
          <h3 style={{ margin: '0 0 4px', fontSize: 'var(--text-base, 16px)', color: 'var(--text-primary)' }}>
            {result.title}
          </h3>
        )}
        {(result?.tagline || result?.wordmark) && (
          <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
            {[result?.tagline, result?.wordmark].filter(Boolean).join('  ·  ')}
          </p>
        )}
      </div>
      <div className="modal-footer">
        <Button variant="secondary" onClick={resetForm}>
          Create another
        </Button>
        <Button variant="primary" onClick={onClose}>
          View in Content
        </Button>
      </div>
    </>
  );

  const renderError = () => (
    <>
      <div className="modal-body">
        <p style={{ color: 'var(--color-danger-600, #dc2626)', fontSize: 'var(--text-sm)' }}>{error}</p>
      </div>
      <div className="modal-footer">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={resetForm}>
          Try again
        </Button>
      </div>
    </>
  );

  return createPortal(
    <div className="modal-overlay" onClick={phase === 'generating' ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>🎞 Reel Studio</h2>
          {phase !== 'generating' && (
            <button className="modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          )}
        </div>
        {phase === 'form' && renderForm()}
        {phase === 'generating' && renderGenerating()}
        {phase === 'done' && renderDone()}
        {phase === 'error' && renderError()}
      </div>
    </div>,
    document.body
  );
}

const phoneFrame: React.CSSProperties = {
  width: 200,
  height: 356,
  borderRadius: 22,
  overflow: 'hidden',
  background: 'var(--bg-secondary)',
  border: '6px solid var(--text-primary)',
  boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
};

const shimmer: React.CSSProperties = {
  width: '100%',
  height: '100%',
  background:
    'linear-gradient(110deg, var(--bg-secondary) 30%, var(--color-primary-100) 50%, var(--bg-secondary) 70%)',
  backgroundSize: '200% 100%',
  animation: 'reelShimmer 1.4s ease-in-out infinite',
};
